import Cookies from 'js-cookie'
import { useEffect, useState } from 'react'

import GithubImage from '@/assets/images/commuity-github.svg?react'
import GoogleImage from '@/assets/images/commuity-google.svg?react'
import { Button, Trans } from '@/components'
import { persistAdAttributionFromCurrentUrl } from '@/utils/adAttribution'
import { API_BASE_URL } from '@/utils/http'
import { getGithubOauthRedirectUri } from '@/utils/oauthRedirect'

export enum OauthType {
  Google,
  Github,
}

const PENDING_OAUTH_TYPE_KEY = 'pending_oauth_type'

export default function useOauth() {
  const [oauthCode, setOauthCode] = useState<string | undefined>()
  const [oauthType, setOauthType] = useState<OauthType | undefined>()

  const hasGoogleScope = (scope: string) => {
    const googleApiScopeRegex = /www\.googleapis\.com/
    return googleApiScopeRegex.test(scope)
  }

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)
    const code = urlParams.get('code')
    const scope = urlParams.get('scope')

    if (code) {
      if (scope && hasGoogleScope(scope)) {
        setOauthCode(code)
        setOauthType(OauthType.Google)
        sessionStorage.removeItem(PENDING_OAUTH_TYPE_KEY)
      } else if (sessionStorage.getItem(PENDING_OAUTH_TYPE_KEY) === 'github') {
        setOauthCode(code)
        setOauthType(OauthType.Github)
        sessionStorage.removeItem(PENDING_OAUTH_TYPE_KEY)
      } else {
        setOauthCode(undefined)
      }
    } else {
      setOauthCode(undefined)
    }
  }, [])

  interface GoogleButtonProps {
    loading?: boolean
    disabled?: boolean
    inviterCode?: string
    gclid?: string
  }

  const GoogleLoginButton = ({ loading, disabled, inviterCode, gclid }: GoogleButtonProps) => {
    return (
      <Button
        block
        type="outline"
        className="flex items-center justify-center gap-2"
        disabled={loading || disabled}
        onClick={() => {
          Cookies.set('inviter_code', inviterCode || '')
          persistAdAttributionFromCurrentUrl()
          if (gclid) {
            Cookies.set('gclid', gclid)
          }
          location.href = `${API_BASE_URL}/oauth_user?oauth_type=google&purpose_type=login`
        }}
      >
        <GoogleImage className="w-6 h-6" />
        <Trans i18nKey="register.google">Continue with Google</Trans>
      </Button>
    )
  }

  const GoogleRegisterButton = ({ loading, disabled, inviterCode, gclid }: GoogleButtonProps) => {
    return (
      <Button
        block
        type="outline"
        className="flex items-center justify-center gap-2"
        disabled={loading || disabled}
        onClick={() => {
          Cookies.set('inviter_code', inviterCode || '')
          persistAdAttributionFromCurrentUrl()
          if (gclid) {
            Cookies.set('gclid', gclid)
          }
          location.href = `${API_BASE_URL}/oauth_user?oauth_type=google&purpose_type=register`
        }}
      >
        <GoogleImage className="w-6 h-6" />
        <Trans i18nKey="register.google">Continue with Google</Trans>
      </Button>
    )
  }

  const GithubLoginButton = ({ loading, inviterCode, gclid }: GoogleButtonProps) => {
    return (
      <Button
        block
        type="outline"
        className="flex items-center justify-center gap-2"
        disabled={loading}
        onClick={() => {
          Cookies.set('inviter_code', inviterCode || '')
          persistAdAttributionFromCurrentUrl()
          if (gclid) {
            Cookies.set('gclid', gclid)
          }
          sessionStorage.setItem(PENDING_OAUTH_TYPE_KEY, 'github')
          const params = new URLSearchParams({
            oauth_type: 'github',
            purpose_type: 'login',
            oauth_redirect_uri: getGithubOauthRedirectUri(),
          })
          location.href = `${API_BASE_URL}/oauth_user?${params.toString()}`
        }}
      >
        <GithubImage className="w-6 h-6 dark:invert dark:brightness-200 dark:contrast-100" />
        <Trans i18nKey="register.github">Continue with Github</Trans>
      </Button>
    )
  }

  return {
    oauthCode,
    oauthType,
    hasGoogleScope,
    GoogleLoginButton,
    GoogleRegisterButton,
    GithubLoginButton,
  }
}
